import type { FunctionComponent } from "react";
import { useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams, useRouter } from "@uirouter/react";
import _ from "lodash";

import { Badge, Tab, TabPanel, Tabs } from "@similarweb/ui-components";

import { i18nFilter } from "app/filters/i18nFilter";
import {
    TabsContainer,
    TabListStyled,
    ContentContainer,
} from "pro-features/pages/conversion/ConversionSegment/ConversionSegmentStyles";
import type { IConversionSegmentProps } from "pro-features/pages/conversion/ConversionSegment/ConversionSegmentTypes";
import { ConversionSegmentOverview } from "pro-features/pages/conversion/ConversionSegmentOverview";
import { ConversionSegmentsUtils } from "app/pages/conversion/ConversionSegmentsUtils";
import { ConversionSegmentOSS } from "app/pages/conversion/oss/ConversionSegmentOSS";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import AllContexts from "../components/AllContexts";

const ConversionSegment: FunctionComponent<IConversionSegmentProps> = (props) => {
    const { translate, getLink, segmentsData } = props;
    const router = useRouter();
    const tabsRefArray = [];
    const [selectedTab, setSelectedTab] = useState<number>(
        parseInt(proNavigator.getApiParams().tab ? proNavigator.getApiParams().tab : "0", 10),
    );
    const { params } = useCurrentStateAndParams();
    const segmentData = ConversionSegmentsUtils.getSegmentById(segmentsData, params.sid);

    // redirect mechanism to avoid showing disabled oss tab for segments with no oss.
    router.transitionService.onStart({}, (transition) => {
        const toParams = transition.params();
        const segmentData = ConversionSegmentsUtils.getSegmentById(segmentsData, toParams.sid);
        const toState = transition.to();
        if (
            toState.name === "conversion-customsegement" &&
            toParams.tab === "1" &&
            !segmentData.ossCountries.includes(parseInt(toParams.country, 10))
        ) {
            proNavigator.go(toState, { ...toParams, tab: undefined });
        }
    });
    useEffect(() => {
        if (params.tab) {
            setSelectedTab(parseInt(params.tab, 10));
        }
    }, [params.tab]);

    const onTabSelect = (index: any) => {
        setSelectedTab(index);
        proNavigator.applyUpdateParams({ ...params, tab: index });
        allTrackers.dispatchTrackEvent(
            "Tab",
            "switch",
            `conversion/segment/analysis/${getTabNameForTracking({ selectedSegmentTab: index.toString() }) || ""}`,
        );
    };

    const segmentOssUserCountries = useMemo(
        () => (segmentData?.ossCountries ? ConversionSegmentsUtils.getSegmentOssUserCountries(segmentData) : []),
        [segmentData],
    );
    const ossTabDisabled = segmentOssUserCountries.length === 0;

    useEffect(() => {
        // if OSS is disabled and route points to tab 1 (OSS), then redirect back to tab 0 (overview)
        if (params.tab === "1" && ossTabDisabled) {
            setTimeout(() => {
                onTabSelect(0);
            });
        }
    }, [params.tab, ossTabDisabled]);

    const getTabProps = (index: number, isDisabled?: boolean) => {
        return {
            onClick: () => (isDisabled ? _.noop() : onTabSelect(index)),
            selected: selectedTab === index,
            key: `tab-${index}`,
        };
    };

    const getTabNameForTracking = ({ selectedSegmentTab }: { selectedSegmentTab: string }) => {
        switch (selectedSegmentTab) {
            case "0":
                return "SEGMENT OVERVIEW";
            case "1":
                return "ON SITE SEARCH";
            default:
                return "SEGMENT OVERVIEW";
        }
    };

    return (
        <AllContexts
            translate={translate}
            track={allTrackers.trackEvent.bind(allTrackers)}
            linkFn={getLink}
        >
            <TabsContainer>
                <FlexRow
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <TabListStyled>
                        <Tab
                            className="firstTab"
                            {...getTabProps(0)}
                        >
                            {i18nFilter()("conversion.segment.overview.tab.title")}
                        </Tab>
                        <Tab
                            disabled={ossTabDisabled}
                            className="secondTab"
                            {...getTabProps(1, ossTabDisabled)}
                        >
                            {i18nFilter()("conversion.segment.onsitesearch.tab.title")}
                            {!ossTabDisabled ? (
                                <span style={{ marginLeft: 8 }}>
                                    <Badge type="new" />
                                </span>
                            ) : null}
                        </Tab>
                    </TabListStyled>
                </FlexRow>
            </TabsContainer>
            <ContentContainer>
                <Tabs
                    onSelect={onTabSelect}
                    selectedIndex={selectedTab}
                    forceRenderTabPanel={true}
                >
                    <TabPanel className="firstTab">
                        <div
                            ref={(el) => {
                                tabsRefArray[0] = el;
                            }}
                        >
                            {segmentData ? (
                                <ConversionSegmentOverview
                                    {...props}
                                    overviewSupportedCountries={segmentData?.countries}
                                />
                            ) : null}
                        </div>
                    </TabPanel>

                    <TabPanel className="secondTab">
                        <div
                            ref={(el) => {
                                tabsRefArray[1] = el;
                            }}
                        >
                            {segmentData ? (
                                <ConversionSegmentOSS
                                    {...props}
                                    ossSupportedCountries={segmentData?.ossCountries}
                                />
                            ) : null}
                        </div>
                    </TabPanel>
                </Tabs>
            </ContentContainer>
        </AllContexts>
    );
};

export default ConversionSegment;
